import { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  NOTIFICATION_LEVEL_ERROR,
  NOTIFICATION_LEVEL_SUCCESS,
} from '@avtechsoftware/ra-javascript-utilities/src/types/NotificationLevels';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import AppTypes from '@avtechsoftware/ra-javascript-utilities/src/types/AppTypes';
import RaaExtremeTempFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/RaaExtremeTempFaultChannel';
import { OperatorTypeDetails } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import moment from 'moment/moment';
import { useHistory, useRouteMatch } from 'react-router-dom';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import IntegerAsBooleanChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/IntegerAsBooleanChannel/IntegerAsBooleanChannel';
import ClassUtility from '../../../../../utility/ClassUtility';
import { pushSessionMessage } from '../../../../../redux/slices/toastNotificationSlice/toastNotificationSlice';
import {
  acknowledgeSingleAlert,
  deleteSingleAlert,
  editSingleAlert,
  fetchSingleAlert,
  selectSingleAlert,
} from '../../../../../redux/slices/singleAlertSlice/singleAlertSlice';
import { selectSessionInformation } from '../../../../../redux/slices/sessionSlice';
import { selectAllTagsByTagIdList } from '../../../../../redux/slices/tagListSlice/tagListSlice';

export const ALERT_DETAILS = 'alert_details';
export const ALERT_EDIT_THRESHOLD = 'alert_edit_threshold';
export const ALERT_EDIT_OPTIONS = 'alert_edit_options';

const useAlertUpdater = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const sessionUserDetails = useSelector(selectSessionInformation);
  const activeAlert = useSelector(selectSingleAlert);
  const tags = useSelector((state) => selectAllTagsByTagIdList(state, activeAlert.tagIds));

  const getEditAlertObj = () => {
    const channel = ChannelService.getChannelGroupChannel(activeAlert.channelKey.channelGroupType,
      activeAlert.channelKey.channelIndex,
      activeAlert.channelKey.channelType);
    const newTriggerConditionValue = channel instanceof BooleanChannel
    || channel instanceof IntegerAsBooleanChannel
      ? activeAlert.triggerCondition.value
      : channel
        .getDisplayValue(activeAlert.triggerCondition.value,
          { scale: activeAlert.temperatureScale });
    return {
      ...activeAlert,
      triggerCondition: { ...activeAlert.triggerCondition, value: newTriggerConditionValue },
      tags,
    };
  };

  const match = useRouteMatch('/s/alerts/:id/edit');
  const initialState = match ? ALERT_EDIT_THRESHOLD : ALERT_DETAILS;

  const [alertDetailsState, setAlertDetailsState] = useState(initialState);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [submitBtnText, setSubmitBtnText] = useState('Save Changes');
  const [editedAlert, setEditedAlert] = useState(getEditAlertObj());

  useEffect(() => {
    setEditedAlert(getEditAlertObj());
  }, [activeAlert.triggerCondition]);

  const handleEditAlertSuccess = (alertId) => {
    setAlertDetailsState(ALERT_DETAILS);
    dispatch(fetchSingleAlert({ alertId }));
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_SUCCESS,
      title: 'Saved',
      body: 'Alert saved successfully.',
    }));
    setIsFormSubmitting(false);
    setSubmitBtnText('Save Changes');
    if (match) {
      history.goBack();
    }
  };

  const handleEditAlertError = () => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_ERROR,
      title: 'Save Error',
      body: 'Error saving Alert.',
    }));
    setIsFormSubmitting(false);
    setSubmitBtnText('Save Changes');
  };

  const cancelEditCondition = () => {
    const initialAlert = getEditAlertObj();
    setEditedAlert(initialAlert);
    if (match) {
      history.goBack();
    }
    setAlertDetailsState(ALERT_DETAILS);
  };

  const toggleEditAlertConditionForm = () => {
    setAlertDetailsState(ALERT_EDIT_THRESHOLD);
  };

  const handleUpdateChannelKey = (newChannelKey) => {
    const updatedAlert = ClassUtility.cloneObject(editedAlert);
    const channel = ChannelService.getChannelGroupChannelByAppType(
      newChannelKey.channelGroupType,
      newChannelKey.channelIndex,
      newChannelKey.channelType,
      AppTypes.CLOUD,
    );
    const initialOperatorTypeId = channel.channelType.thresholdOperators[0];
    if (channel instanceof RaaExtremeTempFaultChannel) {
      updatedAlert.triggerCondition.operator = '>';
    } else {
      updatedAlert.triggerCondition.operator = OperatorTypeDetails[initialOperatorTypeId].symbol;
    }
    updatedAlert.triggerCondition.value = 0;
    updatedAlert.channelKey = newChannelKey;
    setEditedAlert(updatedAlert);
  };

  const handleDeleteAlert = async () => {
    try {
      await dispatch(deleteSingleAlert({ alertId: activeAlert.id }))
        .unwrap();
      dispatch(pushSessionMessage({
        level: NOTIFICATION_LEVEL_SUCCESS,
        title: 'Success',
        body: 'Alert deleted successfully.',
      }));
    } catch {
      dispatch(pushSessionMessage({
        level: NOTIFICATION_LEVEL_ERROR,
        title: 'error',
        body: 'Unexpected error deleting alert.',
      }));
    }
  };

  const handleAcknowledgeAlert = (text) => {
    const params = new URLSearchParams();
    params.append('alertId', activeAlert.id);
    params.append('text', text);
    const triggeredAlertEvent = {
      acknowledgedAt: moment().unix(),
      acknowledgedUserId: sessionUserDetails.id,
      acknowledgedUserFullName: `${sessionUserDetails.firstName} ${sessionUserDetails.lastName}`,
      isAcknowledged: true,
    };
    dispatch(acknowledgeSingleAlert({ alertEvent: triggeredAlertEvent }));
    return axios.post('/alerts/data/acknowledge-alert', params)
      .catch(() => {
        const revertedAlertEvent = {
          acknowledgedAt: null,
          acknowledgedUserId: null,
          acknowledgedUserFullName: null,
          isAcknowledged: false,
        };
        dispatch(acknowledgeSingleAlert({ alertEvent: revertedAlertEvent }));
      });
  };

  const handleSubmitAlertCondition = async (e) => {
    e.preventDefault();
    setIsFormSubmitting(true);
    setSubmitBtnText('Saving...');
    await dispatch(editSingleAlert(editedAlert));
  };

  const handleDeleteAlertAction = (actionId) => {
    const params = new URLSearchParams();
    params.append('alertId', activeAlert.id);
    params.append('alertActionId', actionId);
    const updatedAlert = ClassUtility.cloneObject(activeAlert);
    updatedAlert.alertActionList = updatedAlert.alertActionList
      .filter((action) => action.id !== actionId);
    dispatch(fetchSingleAlert({ alertId: updatedAlert.id }));
    return axios.post('/alerts/data/delete-alert-action', params);
  };

  return {
    handleEditAlertError,
    handleSubmitAlertCondition,
    handleAcknowledgeAlert,
    handleDeleteAlert,
    handleUpdateChannelKey,
    toggleEditAlertConditionForm,
    cancelEditCondition,
    handleEditAlertSuccess,
    alertDetailsState,
    setAlertDetailsState,
    isFormSubmitting,
    submitBtnText,
    editedAlert,
    setEditedAlert,
    handleDeleteAlertAction,
  };
};

export default useAlertUpdater;
